import {
  Box,
  Button,
  Checkbox,
  Container,
  CssBaseline,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { getImageUrl } from '../../helpers/imageHelper';
import { AuthContext } from '../../context/Auth/AuthContext';
import toastError from '../../errors/toastError';
import api from '../../services/api';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    background: 'gray',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    padding: theme.spacing(5),
    maxWidth: 420,
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logo: {
    height: 100,
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 'normal',
    marginBottom: theme.spacing(3),
    color: '#333',
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: '#8a0f0f',
    color: '#fff',
    fontWeight: 'bold',
    padding: theme.spacing(1.5),
    borderRadius: 8,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#a91c1c',
      transform: 'scale(1.03)',
    },
  },
  link: {
    color: '#8a0f0f',
    fontWeight: 500,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  footer: {
    marginTop: theme.spacing(4),
    color: '#fff',
    opacity: 0.8,
  },
}));

const Copyright = ({ companyName, companyUrl }) => (
  <Typography variant="body2" align="center">
    {new Date().getFullYear()} -{' '}
    <Link color="inherit" href={companyUrl}>
      {companyName}
    </Link>
  </Typography>
);

const REMEMBER_LOGIN_KEY = 'rememberLogin';
const REMEMBERED_USER_KEY = 'rememberedLoginUser';

const Login = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [user, setUser] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { handleLogin } = useContext(AuthContext);
  const [submitting, setSubmitting] = useState(false);
  const [theme, setTheme] = useState('light');
  const [companyData, setCompanyData] = useState({
    logo: null,
    name: 'Digitar Informática',
    url: 'https://github.com/rtenorioh/Press-Ticket',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await api.get('/personalizations');
        const config = data?.find((t) => t.theme === theme);
        if (config) {
          setCompanyData(config);
        }
      } catch (err) {
        toastError(err);
      }
    };
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    fetchData();
  }, [theme]);

  useEffect(() => {
    document.title = companyData.name || 'Digitar Informática';
  }, [companyData.name]);

  useEffect(() => {
    const shouldRemember = localStorage.getItem(REMEMBER_LOGIN_KEY) === 'true';
    const rememberedUser = localStorage.getItem(REMEMBERED_USER_KEY);

    if (!shouldRemember || !rememberedUser) return;

    try {
      const parsedUser = JSON.parse(rememberedUser);
      setUser({
        email: parsedUser.email || '',
        password: parsedUser.password || '',
      });
      setRememberMe(true);
    } catch (err) {
      localStorage.removeItem(REMEMBER_LOGIN_KEY);
      localStorage.removeItem(REMEMBERED_USER_KEY);
    }
  }, []);

  const handleChangeInput = (e) =>
    setUser({ ...user, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      await handleLogin(user);
      if (rememberMe) {
        localStorage.setItem(REMEMBER_LOGIN_KEY, 'true');
        localStorage.setItem(REMEMBERED_USER_KEY, JSON.stringify(user));
      } else {
        localStorage.removeItem(REMEMBER_LOGIN_KEY);
        localStorage.removeItem(REMEMBERED_USER_KEY);
      }
    } catch (err) {
      // Toast já tratado no hook de autenticação.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={classes.root}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Paper className={classes.paper} elevation={6}>
          {companyData.logo && (
            <img
              src={getImageUrl(companyData.logo)}
              alt="logo"
              className={classes.logo}
            />
          )}
          <Typography component="h1" variant="h5" className={classes.title}>
            {t('login.title')}
          </Typography>
          <form className={classes.form} noValidate onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              required
              fullWidth
              id="email"
              label={t('login.form.email')}
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              className={classes.textField}
            />
            <TextField
              variant="outlined"
              required
              fullWidth
              name="password"
              label={t('login.form.password')}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              className={classes.textField}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((e) => !e)}
                    >
                      {showPassword ? (
                        <VisibilityOff color="secondary" />
                      ) : (
                        <Visibility color="secondary" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                />
              }
              label="Lembrar-me"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={submitting}
            >
              {t('login.buttons.submit')}
            </Button>
            <Grid container justify="flex-end">
              <Grid item>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  className={classes.link}
                >
                  {t('login.buttons.forgotPassword')}
                </Link>
              </Grid>
            </Grid>
          </form>
        </Paper>
        <Box mt={5} className={classes.footer}>
          <Copyright
            companyName="Digitar Informática"
            companyUrl={companyData.url}
          />
        </Box>
      </Container>
    </div>
  );
};

export default Login;
